import { Controller, useFormContext } from "react-hook-form";
import { DropdownSelect, DropdownSelectOption, ErrorMessage, Loading, Typography } from "@/components/atoms";
import { addToast, clsx, getCountryName } from "@/utils";
import { SignUpDriverFormData } from "../types";
import { useOperator } from "@/hooks";
import { useLazyGetCountryStatesQuery } from "@/redux/apis/resource/resourceApi";
import { useEffect, useState } from "react";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { isEmpty } from "ramda";

export const StateRegisteredField = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<SignUpDriverFormData>();
  const { bookableCountryList, country: defaultCountry } = useOperator();
  const [getCountryStates, { isFetching }] = useLazyGetCountryStatesQuery();
  const [country, setCountry] = useState(defaultCountry);
  const [options, setOptions] = useState<Array<DropdownSelectOption>>([]);

  useEffect(() => {
    getCountryStates(country)
      .unwrap()
      .then(data => setOptions(data.data.states.map(state => ({ name: state.name, value: state.abbreviation }))))
      .catch((e) => {
        getErrorMessages(e).forEach((m) => {
          if (m === "Not found") addToast("danger", "No states found.");
          else addToast("danger", m);
        });
        setOptions([]);
        setValue("stateRegistered", "");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  const getCountryOptions = () => {
    const options: Array<{ name: string; value: string }> = [];
    bookableCountryList.forEach(countryCode => {
      const country = getCountryName(countryCode);
      if (country) options.push({ name: country, value: countryCode });
    })

    return options;
  };

  return (
    <div className={clsx("relative", className)} {...props}>
      {isFetching && <Loading />}
      {bookableCountryList.length > 1 && (
        <div className="flex flex-col gap-y-1 col-span-2 lg:col-span-1">
          <Typography variant="paragraph">Driver Registered Country</Typography>
          <DropdownSelect
            className="w-full"
            placeholder="Select Country"
            options={getCountryOptions()}
            value={country}
            onChange={(value: string) => setCountry(value)}
          />
        </div>
      )}
      <div className="flex flex-col gap-y-1 col-span-2 lg:col-span-1">
        <Typography variant="paragraph">Driver Registered State</Typography>
        <Controller
          name="stateRegistered"
          control={control}
          render={({ field }) => (
            <DropdownSelect
              className="w-full"
              hasError={!!errors.stateRegistered}
              placeholder="Select State"
              options={options}
              value={field.value}
              onChange={(value) => field.onChange(value)}
              disabled={isEmpty(options)}
            />
          )}
        />
        <ErrorMessage errors={errors} name="stateRegistered" />
      </div>
    </div>
  );
};
